import { ContentFilterQueryParamKey, } from '@as-react/common-types';
import { getElasticSearchIndexWithEnv, mergeContentFilterData, normalizeContentFilter, subtractIf, } from '@as-react/common-utils';
import { Logger } from '@as-react/logger';
import { isAxiosError } from 'axios';
import { all, call, fork, getContext, join, put, select, takeLatest } from 'redux-saga/effects';
import contentAPI from '../../../api/content/ContentAPI';
import productsAPI from '../../../api/ProductsAPI';
import { FilterActionTypes, UrlActionTypes } from '../../../enums/ActionEnums';
import { Events } from '../../../enums/AnalyticsEnums';
import { isActiveFilter } from '../../../util/FilterUtil';
import MonetateUtil from '../../../util/MonetateUtil';
import { getListerItemsToShow } from '../../../util/ProductListerUtil';
import { analyticsActions } from '../../analytics/actions/AnalyticsActions.creators';
import { updateCompareProducts } from '../../compare/sagas/CompareSagas';
import { contentOverviewActions } from '../../content/actions/ContentActions.creators';
import { listerActions } from '../../lister/actions/ListerActions.creators';
import { pushRouterState, replaceRouterState, getStoreData, getContentStoreData } from '../../router/sagas/RouterSagas';
import { getWishListProducts } from '../../wishList/sagas/WishListSaga';
import { urlActions } from '../actions/UrlActions.creators';
export function* updatePageAndFetchDataSaga() {
    yield takeLatest(UrlActionTypes.UPDATE_PAGE, updatePageAndFetchData);
}
export function* updateSortAndFetchProductsSaga() {
    yield takeLatest(UrlActionTypes.UPDATE_SORT, updateSortAndFetchProducts);
}
export function* updateStoreIdAndFetchProductsSaga() {
    yield takeLatest(UrlActionTypes.UPDATE_STORE_ID, updateStoreIdAndFetchProducts);
}
export function* updateFiltersAndFetchProductsSaga() {
    yield takeLatest(FilterActionTypes.SET_FILTERS, updateFiltersAndFetchProducts);
    yield takeLatest(FilterActionTypes.REMOVE_FILTER, updateFiltersAndFetchProducts);
    yield takeLatest(FilterActionTypes.REMOVE_ALL_FILTERS, updateFiltersAndFetchProducts);
}
export const getQueryInfo = (state) => state.url;
export const getFredhopperSegment = (state) => state.lister.segment;
export const isListerPage = (state) => !!state.lister;
export const isContentOverviewPage = (state) => !!state.content;
export const getContentPath = (state) => state.content.path;
export const getContentFiltersConfig = (state) => ({
    types: state.content.types,
    categories: state.content.categories,
    filtersPath: state.content.filtersPath,
    hideAllFilters: state.content.hideAllFilters,
    hiddenTags: state.content.hiddenTags,
    typeFilterOptions: state.content.typeFilterOptions,
    typeFilterSortingOptions: state.content.typeFilterSortingOptions,
    categoryFilterOptions: state.content.categoryFilterOptions,
    categoryFilterSortingOptions: state.content.categoryFilterSortingOptions,
    tags: state.content.tags,
});
export const getActiveFilters = (state) => state.filters.activeFilters;
export function* updatePageAndFetchData(action) {
    let queryInfo = yield select(getQueryInfo);
    const isLister = yield select(isListerPage);
    const isContentOverview = yield select(isContentOverviewPage);
    if (isLister) {
        queryInfo = Object.assign(Object.assign({}, queryInfo), { page: action.payload });
        yield put(urlActions.setUrlParams(queryInfo));
        yield fork(fetchProducts, queryInfo, action);
    }
    else if (isContentOverview) {
        queryInfo = Object.assign(Object.assign({}, queryInfo), { page: action.payload });
        yield put(urlActions.setUrlParams(queryInfo));
        yield fork(fetchContent, queryInfo);
    }
}
export function* updateSortAndFetchProducts(action) {
    let queryInfo = yield select(getQueryInfo);
    queryInfo = Object.assign(Object.assign({}, queryInfo), { sort: action.payload, page: 0 });
    yield put(urlActions.setUrlParams(queryInfo));
    yield put(listerActions.showPlaceholderTiles());
    yield put(analyticsActions.listerSortChange(action.payload));
    yield fork(fetchProducts, queryInfo, action);
}
export function* updateStoreIdAndFetchProducts(action) {
    let queryInfo = yield select(getQueryInfo);
    queryInfo = Object.assign(Object.assign({}, queryInfo), { storeId: queryInfo.storeId === action.payload ? undefined : action.payload, page: 0 });
    yield put(urlActions.setUrlParams(queryInfo));
    yield put(listerActions.showPlaceholderTiles());
    yield fork(fetchProducts, queryInfo, action);
}
export function* updateFiltersAndFetchProducts(action) {
    var _a;
    const queryInfo = yield select(getQueryInfo);
    const isLister = yield select(isListerPage);
    const isContentOverview = yield select(isContentOverviewPage);
    if (isLister) {
        yield put(listerActions.showPlaceholderTiles());
        yield fork(fetchProducts, queryInfo, action);
    }
    else if (isContentOverview) {
        const shouldUpdateFilters = action.type === FilterActionTypes.REMOVE_ALL_FILTERS ||
            ((_a = action === null || action === void 0 ? void 0 : action.payload) === null || _a === void 0 ? void 0 : _a.queryParamKey) ===
                ContentFilterQueryParamKey.CATEGORY;
        yield fork(fetchContent, queryInfo, shouldUpdateFilters);
    }
}
export function* fetchProducts(queryInfo, action) {
    try {
        const aemConfigurationContext = yield getContext('aemConfiguration');
        const { currency, pageInfo, authenticationConfig, fredHopperSearchUrl, isShopLanding } = aemConfigurationContext;
        const defaultListerSort = aemConfigurationContext.lister.sort.defaultValue;
        const pushingRouterStateTask = yield fork(pushRouterState, queryInfo);
        yield join(pushingRouterStateTask);
        const fredhopperSegment = yield select(getFredhopperSegment);
        const activeFilters = yield select(getActiveFilters);
        const searchResult = yield call(productsAPI.fetchProducts, queryInfo, fredHopperSearchUrl, defaultListerSort, authenticationConfig, fredhopperSegment, isShopLanding);
        switch (action.type) {
            case FilterActionTypes.SET_FILTERS:
            case FilterActionTypes.REMOVE_FILTER:
            case FilterActionTypes.REMOVE_ALL_FILTERS:
            case UrlActionTypes.UPDATE_STORE_ID: {
                const mappedFilters = activeFilters
                    ? searchResult.filters.map(filter => (Object.assign(Object.assign({}, filter), { isActive: isActiveFilter(filter, activeFilters) })))
                    : searchResult.filters;
                yield put(listerActions.updateExistingFilters(mappedFilters));
                yield put(listerActions.updateProductsAndTotalPagesAndItemCount(searchResult.items, searchResult.totalPages, searchResult.itemCount));
                break;
            }
            default:
                yield put(listerActions.updateProducts(searchResult.items));
                yield put(analyticsActions.listerViewProductList(searchResult.items, currency, pageInfo.isSearchPage ? Events.VIEW_SEARCH_RESULTS : Events.VIEW_PRODUCT_LIST, queryInfo.q));
        }
        const partialRootState = yield select(getStoreData);
        yield fork(updateCompareProducts);
        yield fork(replaceRouterState, partialRootState.url, partialRootState);
        yield fork(getWishListProducts);
        const listerItemsToShow = getListerItemsToShow(searchResult.items, queryInfo.size, true);
        MonetateUtil.pushListerEvent(listerItemsToShow.filter(item => item.productId));
    }
    catch (error) {
        if (!isAxiosError(error)) {
            Logger.logError(error);
        }
    }
}
export function* fetchContent(queryInfo, shouldUpdateFilters = false) {
    var _a, _b;
    try {
        const aemConfigurationContext = yield getContext('aemConfiguration');
        const { defaultRequestParams, servicesEndpoint, activeEnvironment, elasticsearchIndex } = aemConfigurationContext;
        const elasticSearchIndexWithEnv = getElasticSearchIndexWithEnv(activeEnvironment, elasticsearchIndex);
        const contentFiltersConfig = yield select(getContentFiltersConfig);
        const contentPath = yield select(getContentPath);
        let tags = [];
        if (contentFiltersConfig.tags) {
            tags = tags.concat(contentFiltersConfig.tags);
        }
        if (queryInfo.category) {
            tags.push(queryInfo.category);
        }
        if (queryInfo.type) {
            tags = tags.concat(queryInfo.type);
        }
        const pushingRouterStateTask = yield fork(pushRouterState, queryInfo);
        yield join(pushingRouterStateTask);
        const content = yield call(contentAPI.getContentFromElasticSearch, defaultRequestParams, servicesEndpoint, elasticSearchIndexWithEnv, queryInfo.page, queryInfo.size, contentPath, queryInfo.searchTerm, tags.length > 0 ? tags : undefined);
        if (shouldUpdateFilters) {
            const filtersData = yield call(contentAPI.getFilters, servicesEndpoint, defaultRequestParams.mainWebshop, contentFiltersConfig.filtersPath, queryInfo.category, queryInfo.type);
            if (!contentFiltersConfig.hideAllFilters) {
                const shouldHideItem = (item, hiddenTag) => item === null || item === void 0 ? void 0 : item.value.startsWith(hiddenTag);
                filtersData.categories = subtractIf(filtersData.categories, (contentFiltersConfig === null || contentFiltersConfig === void 0 ? void 0 : contentFiltersConfig.hiddenTags) || [], shouldHideItem);
                filtersData.types = subtractIf(filtersData.types, (contentFiltersConfig === null || contentFiltersConfig === void 0 ? void 0 : contentFiltersConfig.hiddenTags) || [], shouldHideItem);
            }
            const hideCategoryFilter = contentFiltersConfig.hideAllFilters || !((_a = filtersData === null || filtersData === void 0 ? void 0 : filtersData.categories) === null || _a === void 0 ? void 0 : _a.length);
            const hideTypeFilter = contentFiltersConfig.hideAllFilters || !((_b = filtersData === null || filtersData === void 0 ? void 0 : filtersData.types) === null || _b === void 0 ? void 0 : _b.length);
            const filters = [];
            if (!hideCategoryFilter) {
                const categoriesFilterData = mergeContentFilterData(filtersData.categories, contentFiltersConfig.categories);
                if (categoriesFilterData.length > 0) {
                    filters.push(normalizeContentFilter(categoriesFilterData, FilterActionTypes.LINK_FILTER, ContentFilterQueryParamKey.CATEGORY, 'content.overview.tag.selection.title.department', contentFiltersConfig.categoryFilterSortingOptions, contentFiltersConfig.categoryFilterOptions, [queryInfo.category]));
                }
            }
            if (!hideTypeFilter) {
                const typesFilterData = mergeContentFilterData(filtersData.types, contentFiltersConfig.types);
                if (typesFilterData.length > 0) {
                    filters.push(normalizeContentFilter(typesFilterData, FilterActionTypes.MULTISELECT_FILTER, ContentFilterQueryParamKey.TYPE, 'content.overview.tag.selection.title.type', contentFiltersConfig.typeFilterSortingOptions, contentFiltersConfig.typeFilterOptions, queryInfo.type));
                }
            }
            yield put(contentOverviewActions.updaterFiltersAction(filters));
        }
        yield put(contentOverviewActions.showPlaceholderTiles());
        yield put(contentOverviewActions.updaterContentAction(content));
        const partialRootState = yield select(getContentStoreData);
        yield fork(replaceRouterState, partialRootState.url, partialRootState);
    }
    catch (error) {
        if (!isAxiosError(error)) {
            Logger.logError(error);
        }
    }
}
export default function* urlSaga() {
    yield all([
        updatePageAndFetchDataSaga(),
        updateSortAndFetchProductsSaga(),
        updateStoreIdAndFetchProductsSaga(),
        updateFiltersAndFetchProductsSaga(),
    ]);
}
